// lineVerdict.ts — the words a line's order context earns, and the banner
// sentences that name the lines. Pure: no classes, no React, so the copy can be
// read and tested on its own.

import { formatCents, formatUnitPrice, LinePoContext, VendorInvoiceLine } from './apInvoicesApi';

export type VerdictTone = 'amber' | 'neutral';

export interface VerdictNote {
  kind: 'over_billed' | 'return_pending' | 'exceeds_remaining';
  tone: VerdictTone;
  text: string;
}

/** Units as a person reads them: "2,100", "0.5". */
export function formatUnits(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—';
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function unitsPhrase(n: number): string {
  const rounded = Number(n.toFixed(2));
  return `${rounded.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${rounded === 1 ? 'unit' : 'units'}`;
}

/** A cents figure that may carry sub-cent digits, as a per-unit price. */
function unitPrice(cents: number): string {
  return formatUnitPrice(String(cents));
}

function priceSentence(ctx: LinePoContext): string | null {
  const v = ctx.verdict;
  if (v.price_variance_cents === null || v.invoice_unit_price_cents === null || ctx.unit_cost_cents === null) {
    return null;
  }
  return `This invoice bills ${unitPrice(v.invoice_unit_price_cents)} per unit; the order says ${unitPrice(ctx.unit_cost_cents)}.`;
}

/**
 * What to say under one line. Empty when the line bills within what arrived
 * and within what the order line has left: silence is the signal.
 */
export function verdictNotes(ctx: LinePoContext | null | undefined): VerdictNote[] {
  if (!ctx) return [];
  const v = ctx.verdict;
  const notes: VerdictNote[] = [];
  const billed = ctx.billed_before_cents + ctx.this_invoice_cents;
  // The server's own subtraction, run backwards, so the two figures in the
  // sentence always differ by exactly the beyond amount it computed.
  const receivedValue = billed - v.beyond_cents;

  if (v.ahead_of_receiving && v.ahead_reason === 'over_billed') {
    const head = `Billed ${formatCents(billed)} against ${formatCents(receivedValue)} received`;
    const text = v.beyond_units === null
      ? `${head}.`
      : `${head}: ${unitsPhrase(v.beyond_units)} beyond at the order price.`;
    const price = priceSentence(ctx);
    notes.push({ kind: 'over_billed', tone: 'amber', text: price ? `${text} ${price}` : text });
  } else if (v.ahead_of_receiving && v.ahead_reason === 'return_pending') {
    const back = ctx.pending_return_quantity > 0
      ? `Return in progress: ${unitsPhrase(ctx.pending_return_quantity)} back with the vendor.`
      : 'Return in progress.';
    notes.push({
      kind: 'return_pending',
      tone: 'neutral',
      text: `${back} Billed ${formatCents(billed)} against ${formatCents(receivedValue)} received.`,
    });
  }

  if (v.exceeds_remaining) {
    const leftBefore = Math.max(0, ctx.line_value_cents - ctx.billed_before_cents);
    let text = `Bills ${formatCents(v.exceeds_by_cents)} over what this order line has left (${formatCents(leftBefore)} left before this invoice).`;
    const price = notes.some((n) => n.kind === 'over_billed') ? null : priceSentence(ctx);
    if (price) text = `${text} ${price}`;
    notes.push({ kind: 'exceeds_remaining', tone: 'amber', text });
  }
  return notes;
}

/** "Line 2", "Lines 2 and 5", "Lines 2, 3 and 5". */
export function namedLines(numbers: number[]): string {
  const sorted = Array.from(new Set(numbers)).sort((a, b) => a - b);
  if (sorted.length === 1) return `Line ${sorted[0]}`;
  const head = sorted.slice(0, -1).join(', ');
  return `Lines ${head} and ${sorted[sorted.length - 1]}`;
}

const BANNER_PREDICATES: Record<string, (ctx: LinePoContext) => boolean> = {
  bills_beyond_received: (c) => c.verdict.ahead_of_receiving && c.verdict.ahead_reason === 'over_billed',
  return_pending: (c) => c.verdict.ahead_of_receiving && c.verdict.ahead_reason === 'return_pending',
  exceeds_remaining: (c) => c.verdict.exceeds_remaining,
};

/**
 * The banner body for a line-level attention code, built from the lines that
 * carry it. null when the code is not line-level, or when no line carries the
 * matching verdict (an invoice read before the lines had order context): the
 * caller then prints the server's own message.
 */
export function bannerSentence(code: string, lines: VendorInvoiceLine[]): string | null {
  const pred = BANNER_PREDICATES[code];
  if (!pred) return null;
  const hits = lines.filter((l) => l.po_context && pred(l.po_context)).map((l) => l.line_number);
  if (hits.length === 0) return null;
  const one = new Set(hits).size === 1;
  const who = namedLines(hits);
  const bill = one ? 'bills' : 'bill';
  const tail = 'Details on each line below.';
  if (code === 'bills_beyond_received') return `${who} ${bill} beyond what has arrived. ${tail}`;
  if (code === 'return_pending') return `${who} ${bill} units that are back with the vendor on a return. ${tail}`;
  return one
    ? `${who} bills more than its order line has left. ${tail}`
    : `${who} bill more than their order lines have left. ${tail}`;
}
